import { Button } from "@common/components/Atoms/Button";
import Error from "@common/components/Atoms/Error";
import Loading from "@common/components/Atoms/Loading";
import Modal from "@common/components/Molecules/Modal";
import { JobService } from "@pro/_services";
import React from "react";

const CannotLeave = (jobName: string) => (
    <div className="c-block">
        <h1 className="c-block__title">cannot remove</h1>
        <div className="c-block__body">
            <p>you can't be removed from this job because you have referrals.</p>
            <p>
                to remove your referrals and yourself, please{" "}
                <a
                    className="c-link c-link--magenta"
                    rel="noopener noreferrer"
                    target="_blank"
                    href={`mailto:support@relode.com?subject=Request Removal From ${jobName}`}>
                    contact
                </a>{" "}
                relode.
            </p>
        </div>
    </div>
);

interface ConfirmLeaveProps {
    jobId: number;
    onOkay: () => void;
    onCancel: () => void;
}

interface ConfirmLeaveState {
    loading: boolean;
    canLeave: boolean;
    error: boolean;
    errorMessages: string[];
}

class ConfirmLeave extends React.Component<ConfirmLeaveProps, ConfirmLeaveState> {
    constructor(props) {
        super(props);
        this.leaveJob = this.leaveJob.bind(this);
        this.state = {
            canLeave: false,
            loading: false,
            error: false,
            errorMessages: [] as string[],
        };
    }

    leaveJob() {
        this.setState({ loading: true }, () =>
            JobService.leave(this.props.jobId)
                .then(() => this.props.onOkay())
                .catch(err => handleError(err, this))
        );
    }

    render() {
        if (this.state.loading) {
            return <Loading />;
        }

        return (
            <div>
                <div className="c-block">
                    <h1 className="c-block__title">remove from my jobs?</h1>
                    <div className="c-block__body">
                        <p>this job will disappear from your account.</p>
                    </div>
                </div>

                <div className="c-block">
                    <Error
                        show={this.state.error}
                        description={
                            this.state.errorMessages && this.state.errorMessages.length
                                ? this.state.errorMessages[0]
                                : "an error occurred"
                        }
                    />
                </div>

                <div className="c-modal__footer">
                    <div className="c-modal__footer-left">
                        <Button id="can-leave-yes-button" green={true} onClick={this.leaveJob}>
                            yes
                        </Button>
                    </div>
                    <div className="c-modal__footer-right">
                        <Button id="can-leave-no-button" onClick={this.props.onCancel}>
                            no
                        </Button>
                    </div>
                </div>
            </div>
        );
    }
}

interface LeaveJobProps {
    isOpen: boolean;
    closeModal: () => void;
    onOkay: () => void;
    onCancel: () => void;
    jobId: number;
    jobName: string;
}

interface LeaveJobState {
    loading: boolean;
    canLeave: boolean;
    error: boolean;
    errorMessages: string[];
}

export class LeaveJob extends React.Component<LeaveJobProps, LeaveJobState> {
    private _abortControllers: { [key: string]: AbortController } = {};

    constructor(props) {
        super(props);
        this.state = {
            canLeave: false,
            loading: true,
            error: false,
            errorMessages: [] as string[],
        };
        this.checkCanLeaveJob = this.checkCanLeaveJob.bind(this);
    }

    componentWillReceiveProps(newProps) {
        if (newProps.isOpen && newProps.isOpen !== this.props.isOpen) {
            this.checkCanLeaveJob(newProps.jobId);
        }
    }

    componentWillUnmount() {
        const abortControllers = this._abortControllers;
        if (Object.keys(abortControllers).length > 0) {
            Object.keys(abortControllers).map(key => abortControllers[key].abort());
        }
    }

    checkCanLeaveJob(jobId) {
        // Check to see if there already was an abortController, and if so, call abort on it
        if (this._abortControllers.canLeave) {
            this._abortControllers.canLeave.abort();
        }
        this._abortControllers.canLeave = new AbortController();
        const signal = this._abortControllers.canLeave.signal;

        JobService.canLeave(jobId, signal)
            .then(canLeave => {
                this.setState({ canLeave, loading: false });
            })
            .catch(err => handleError(err, this));
    }

    render() {
        if (this.props.isOpen && this.state.loading) {
            return <Loading />;
        }

        return (
            <Modal
                title={this.props.jobName}
                isOpen={this.props.isOpen}
                closeModal={this.props.closeModal}>
                {this.state.canLeave ? (
                    <ConfirmLeave {...this.props} />
                ) : (
                    <CannotLeave {...this.props.jobName} />
                )}
            </Modal>
        );
    }
}

// Helpers...

function handleError(err, stateContainer) {
    if (err.type === "abort" || err.name === "AbortError") {
        stateContainer.setState({ loading: false });
        return;
    }

    const messages = err.responseData ? err.responseData.error_messages : [err.message];

    console.log(messages);
    stateContainer.setState({
        loading: false,
        error: true,
        errorMessages: messages,
    });
}
